import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

class HegichiCartUpsell extends HTMLElement {
  connectedCallback() {
    if (this.hasAcceptedUpsell()) {
      this.remove();
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.isLoading = false;
    this.reloadQueued = false;

    this.observer?.disconnect();
    this.observer = new MutationObserver(() => this.queueReloadIfReset());
    this.observer.observe(this, {
      childList: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });

    this.loadRecommendations();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
  }

  hasAcceptedUpsell() {
    try {
      return sessionStorage.getItem('hegichi-cart-upsell-accepted') === 'true';
    } catch {
      return false;
    }
  }

  rememberAcceptedUpsell() {
    try {
      sessionStorage.setItem('hegichi-cart-upsell-accepted', 'true');
    } catch {
      // The upsell still hides for this render if storage is unavailable.
    }
  }

  queueReloadIfReset() {
    if (this.hasAcceptedUpsell()) {
      this.observer?.disconnect();
      this.remove();
      return;
    }

    if (this.reloadQueued) return;

    this.reloadQueued = true;
    queueMicrotask(() => {
      this.reloadQueued = false;

      if (!this.isConnected || this.isLoading) return;

      const hasRecommendation = Boolean(this.querySelector('[data-upsell-card]'));
      if (this.hidden && !hasRecommendation) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.loadRecommendations();
      }
    });
  }

  async loadRecommendations() {
    if (this.isLoading) return;
    this.isLoading = true;

    const productId = this.dataset.productId;
    const recommendationsUrl = this.dataset.recommendationsUrl;

    if (!productId || !recommendationsUrl) {
      this.remove();
      return;
    }

    const url = new URL(recommendationsUrl, window.location.origin);
    url.searchParams.set('section_id', 'hegichi-cart-upsell');
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '4');
    url.searchParams.set('intent', 'related');

    try {
      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) throw new Error('Could not load recommendations.');

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const results = documentFragment.querySelector('[data-upsell-results]');

      if (!results) {
        this.remove();
        return;
      }

      this.replaceChildren(results);
      const cartProductIds = new Set(
        (this.dataset.cartProductIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      );
      const cards = Array.from(this.querySelectorAll('[data-upsell-card]'));
      const chosenCard = cards.find((card) => !cartProductIds.has(card.dataset.productId || ''));

      if (!chosenCard) {
        this.remove();
        return;
      }

      cards.forEach((card) => {
        if (card !== chosenCard) card.remove();
      });

      this.hidden = false;
      this.bindCard(chosenCard);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('[Hegichi cart upsell]', error);
      }
      this.remove();
    } finally {
      this.isLoading = false;
    }
  }

  bindCard(card) {
    const variantControl = card.querySelector('[data-upsell-variant]');
    const button = card.querySelector('[data-upsell-add]');

    if (variantControl instanceof HTMLSelectElement) {
      variantControl.addEventListener('change', () => this.updateVariantPresentation(card, variantControl));
      this.updateVariantPresentation(card, variantControl);
    }

    button?.addEventListener('click', () => this.addRecommendedProduct(card));

    this.querySelector('[data-upsell-dismiss]')?.addEventListener('click', () => {
      this.rememberAcceptedUpsell();
      this.observer?.disconnect();
      this.remove();
    });
  }

  updateVariantPresentation(card, select) {
    const selectedOption = select.selectedOptions[0];
    const price = card.querySelector('[data-upsell-price]');
    const image = card.querySelector('.hegichi-cart-upsell__image');

    if (price && selectedOption?.dataset.price) {
      price.textContent = selectedOption.dataset.price;
    }

    if (image instanceof HTMLImageElement && selectedOption?.dataset.image) {
      image.src = selectedOption.dataset.image;
      image.removeAttribute('srcset');
      image.alt = selectedOption.dataset.imageAlt || image.alt;
    }
  }

  async addRecommendedProduct(card) {
    const variantControl = card.querySelector('[data-upsell-variant]');
    const button = card.querySelector('[data-upsell-add]');
    const message = card.querySelector('[data-upsell-message]');
    const buttonText = card.querySelector('[data-upsell-button-text]');
    const variantId = variantControl?.value;

    if (!(button instanceof HTMLButtonElement) || !variantId) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (buttonText) buttonText.textContent = 'Adding…';
    if (message) message.textContent = '';

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);

    if (sectionIds.length > 0) {
      formData.append('sections', Array.from(new Set(sectionIds)).join(','));
      formData.append('sections_url', window.location.pathname + window.location.search);
    }

    const request = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...request,
        headers: {
          ...request.headers,
          Accept: 'text/html',
        },
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'This option could not be added.');
      }

      this.rememberAcceptedUpsell();
      this.observer?.disconnect();
      this.hidden = true;

      this.dispatchEvent(
        new CartAddEvent({}, variantId.toString(), {
          source: 'hegichi-cart-upsell',
          itemCount: 1,
          productId: button.dataset.productId,
          variantId,
          sections: result.sections,
        })
      );

      if (buttonText) buttonText.textContent = 'Added';
      const liveRegion = this.querySelector('[data-upsell-live-region]');
      if (liveRegion) liveRegion.textContent = 'Recommended item added to your cart.';
    } catch (error) {
      if (message) message.textContent = error.message || 'Please try again.';
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (buttonText) buttonText.textContent = 'Quick add';
    }
  }
}

if (!customElements.get('hegichi-cart-upsell')) {
  customElements.define('hegichi-cart-upsell', HegichiCartUpsell);
}
